import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDeferredRender } from './FBDeferredRender.context';

interface FBDeferredRenderProps {
  order: number
  immediate?: boolean
  ready?: boolean
  minHeight?: number
  placeholder?: React.ReactNode
}

const FBDeferredRender: React.FC<FBDeferredRenderProps> = ({
  order,
  immediate = false,
  ready = true,
  placeholder,
  children,
}) => {
  const context = useDeferredRender();
  const scheduler = context?.scheduler;
  const eager = !scheduler || immediate;
  const [admitted, setAdmitted] = useState(ready && eager);
  const token = useRef(Symbol('deferred-render')).current;
  const placeholderRef = useRef<HTMLDivElement>(null);
  const mounted = admitted || (ready && eager);

  // Latch eager admissions so later focus/ordering changes never demote them.
  useLayoutEffect(() => {
    if (mounted && !admitted) { setAdmitted(true); }
  }, [mounted, admitted]);

  useEffect(() => {
    if (mounted || !ready || !scheduler || !placeholderRef.current) { return; }
    scheduler.register(token, order, placeholderRef.current, () => setAdmitted(true));
    return () => scheduler.unregister(token);
    // Order changes update the existing registration below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, ready, scheduler, token]);

  useEffect(() => {
    scheduler?.updateOrder(token, order);
  }, [scheduler, token, order]);

  return mounted ? <>{children}</> : (
    <div ref={placeholderRef} aria-busy="true">
      {placeholder}
    </div>
  );
};

export default FBDeferredRender;
